import { z } from 'zod';

// Common name validation - allows letters, spaces, hyphens, apostrophes (for names like O'Brien, Mary-Jane)
const nameRegex = /^[a-zA-ZÀ-ÿ]+([\s'-][a-zA-ZÀ-ÿ]+)*$/;

// Block common spam/test patterns
const spamPatterns = [
  /^test$/i,
  /^user$/i,
  /^admin$/i,
  /^asdf/i,
  /^qwerty/i,
  /^abc123/i,
  /^name$/i,
  /^firstname$/i,
  /^lastname$/i,
  /(.)\1{4,}/, // 5+ repeated characters (aaaaaaa)
];

function isSpamName(name) {
  const trimmed = name.trim().toLowerCase();
  return spamPatterns.some(pattern => pattern.test(trimmed));
}

// Password validation helpers
export function getPasswordStrength(password) {
  if (!password) return { score: 0, label: 'Too weak', color: 'bg-danger' };

  let score = 0;

  // Length checks
  if (password.length >= 8) score += 1;
  if (password.length >= 12) score += 1;
  if (password.length >= 16) score += 1;

  // Character type checks
  if (/[a-z]/.test(password)) score += 1;
  if (/[A-Z]/.test(password)) score += 1;
  if (/[0-9]/.test(password)) score += 1;
  if (/[^a-zA-Z0-9]/.test(password)) score += 1;

  // Determine label and color
  if (score <= 2) return { score: 1, label: 'Weak', color: 'bg-danger' };
  if (score <= 4) return { score: 2, label: 'Fair', color: 'bg-warning' };
  if (score <= 5) return { score: 3, label: 'Good', color: 'bg-primary' };
  return { score: 4, label: 'Strong', color: 'bg-success' };
}

// Display name schema (reusable)
export const displayNameSchema = z
  .string()
  .min(2, 'Name must be at least 2 characters')
  .max(50, 'Name must be less than 50 characters')
  .refine(
    (val) => val.trim().length >= 2,
    'Name cannot be only spaces'
  )
  .refine(
    (val) => nameRegex.test(val.trim()),
    'Name can only contain letters, spaces, hyphens, and apostrophes'
  )
  .refine(
    (val) => !isSpamName(val),
    'Please enter a valid name'
  )
  .transform((val) => val.trim());

// Password schema with strength requirements
export const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .refine(
    (val) => /[a-z]/.test(val),
    'Password must contain at least one lowercase letter'
  )
  .refine(
    (val) => /[A-Z]/.test(val),
    'Password must contain at least one uppercase letter'
  )
  .refine(
    (val) => /[0-9]/.test(val),
    'Password must contain at least one number'
  );

// Email with additional checks
export const emailSchema = z
  .string()
  .email('Invalid email address')
  .refine(
    (val) => !val.includes('..'),
    'Invalid email address'
  )
  .refine(
    (val) => val.length <= 254,
    'Email address is too long'
  )
  .transform((val) => val.toLowerCase().trim());

export const signupSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  displayName: displayNameSchema,
});

export const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

export const phoneSchema = z.object({
  phone: z.string().regex(/^\+353[0-9]{9}$/, 'Invalid Irish phone number'),
});

export const otpSchema = z.object({
  code: z.string().length(6, 'Code must be 6 digits'),
});

export const raffleSchema = z.object({
  title: z.string().min(5, 'Title too short').max(80, 'Title too long'),
  description: z.string().min(20, 'Description too short').max(2000, 'Description too long'),
  category: z.enum(['electronics', 'furniture', 'gaming', 'fashion', 'sports', 'collectibles', 'home', 'toys', 'music', 'books', 'vehicles', 'other']),
  condition: z.enum(['new', 'likeNew', 'good', 'fair']),
  photos: z.array(z.string().url()).min(1, 'At least 1 photo required').max(8, 'Maximum 8 photos'),
  ticketPrice: z.number().min(100, 'Minimum €1').max(50000, 'Maximum €500'),
  ticketQuantity: z.number().min(10, 'Minimum 10 tickets').max(1000, 'Maximum 1000 tickets'),
  minimumThreshold: z.number().min(50).max(100),
  endsAt: z.date().refine((d) => d > new Date(), 'End date must be in the future'),
  pickupArea: z.string().min(2, 'Pickup area required'),
  pickupAddress: z.string().min(10, 'Full address required'),
});

export const entrySchema = z.object({
  ticketCount: z.number().min(1).max(10),
  skillQuestionId: z.string(),
  skillAnswer: z.number().min(0).max(2),
});

export const disputeSchema = z.object({
  reason: z.enum(['notAsDescribed', 'notReceived', 'sellerUnresponsive', 'winnerUnresponsive', 'damaged']),
  description: z.string().min(20, 'Please provide more details').max(1000),
  evidence: z.array(z.string().url()).optional(),
});

export const reviewSchema = z.object({
  rating: z.number().min(1).max(5),
  text: z.string().max(500).optional(),
});

export const messageSchema = z.object({
  text: z.string().min(1, 'Message cannot be empty').max(1000, 'Message too long'),
});

// Profile update schema (for Settings page)
export const profileUpdateSchema = z.object({
  displayName: displayNameSchema,
  location: z
    .string()
    .max(100, 'Location must be less than 100 characters')
    .optional()
    .transform((val) => val?.trim() || ''),
});
